import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { Package, MapPin, Phone, User, Truck, Store, Building2 } from 'lucide-react';
import { getShippingRate } from '../config/shippingRates';
import { trackInitiateCheckout } from '../utils/metaPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WILAYAS = [
  'Adrar', 'Chlef', 'Laghouat', 'Oum El Bouaghi', 'Batna', 'Béjaïa', 'Biskra', 'Béchar',
  'Blida', 'Bouira', 'Tamanrasset', 'Tébessa', 'Tlemcen', 'Tiaret', 'Tizi Ouzou', 'Alger',
  'Djelfa', 'Jijel', 'Sétif', 'Saïda', 'Skikda', 'Sidi Bel Abbès', 'Annaba', 'Guelma',
  'Constantine', 'Médéa', 'Mostaganem', 'M’Sila', 'Mascara', 'Ouargla', 'Oran', 'El Bayadh',
  'Illizi', 'Bordj Bou Arreridj', 'Boumerdès', 'El Tarf', 'Tindouf', 'Tissemsilt', 'El Oued',
  'Khenchela', 'Souk Ahras', 'Tipaza', 'Mila', 'Aïn Defla', 'Naama', 'Aïn Témouchent',
  'Ghardaïa', 'Relizane', 'Timimoun', 'Bordj Badji Mokhtar', 'Ouled Djellal', 'Béni Abbès',
  'In Salah', 'In Guezzam', 'Touggourt', 'Djanet', 'El M’Ghair', 'El Meniaa'
];

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '',
    email: '',
    address: '',
    wilaya: '',
    commune: '',
    delivery_method: 'home_delivery' // home_delivery, stop_desk, pickup
  });

  // Calculer les frais de livraison quand wilaya ou delivery_method change
  useEffect(() => {
    if (formData.wilaya && formData.delivery_method) {
      const cost = getShippingRate(formData.wilaya, formData.delivery_method);
      setShippingCost(cost);
    } else {
      setShippingCost(0);
    }
  }, [formData.wilaya, formData.delivery_method]);

  // Meta Pixel — track InitiateCheckout once when arriving on this page with items
  useEffect(() => {
    if (cartItems.length > 0) {
      trackInitiateCheckout(cartItems, getCartTotal());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getFinalTotal = () => {
    return getCartTotal() + shippingCost;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        ...formData,
        items: cartItems.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        subtotal: getCartTotal(),
        shipping_cost: shippingCost,
        total: getFinalTotal()
      };

      const response = await axios.post(`${API}/orders`, orderData);
      
      // Navigate FIRST, then clear cart
      const orderId = response.data.id;
      navigate(`/order-success/${orderId}`);
      
      // Clear cart after navigation
      setTimeout(() => {
        clearCart();
      }, 100);
      
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Erreur lors de la création de la commande. Veuillez réessayer.');
      setLoading(false);
    }
  };

  // Allow checkout page to load even if cart is empty (during order submission)
  // Only redirect if cart is empty AND we're not currently submitting
  useEffect(() => {
    if (cartItems.length === 0 && !loading) {
      navigate('/cart');
    }
  }, [cartItems, loading, navigate]);

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-8" data-testid="checkout-title">
          Finaliser la commande
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              {/* Customer Info */}
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Informations personnelles
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-500 mb-2">Nom complet *</label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:border-cyan-500 transition"
                      placeholder="Votre nom complet"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:border-cyan-500 transition"
                      placeholder="0552 10 84 88"
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 mb-2">
                      Email <span className="text-xs text-gray-400">(facultatif — pour recevoir les promos)</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:border-cyan-500 transition"
                      placeholder="votre@email.com"
                      data-testid="email-input"
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Method */}
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Méthode de récupération
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                    formData.delivery_method === 'home_delivery'
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-gray-300 hover:border-blue-500'
                  }`}>
                    <input
                      type="radio"
                      name="delivery_method"
                      value="home_delivery"
                      checked={formData.delivery_method === 'home_delivery'}
                      onChange={handleChange}
                      className="text-cyan-400"
                      data-testid="home-delivery-option"
                    />
                    <Truck className="w-6 h-6 text-cyan-400" />
                    <div>
                      <div className="text-gray-900 font-semibold">À domicile</div>
                      <div className="text-gray-500 text-sm">World Express</div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                    formData.delivery_method === 'stop_desk'
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-gray-300 hover:border-blue-500'
                  }`}>
                    <input
                      type="radio"
                      name="delivery_method"
                      value="stop_desk"
                      checked={formData.delivery_method === 'stop_desk'}
                      onChange={handleChange}
                      className="text-cyan-400"
                      data-testid="stop-desk-option"
                    />
                    <Building2 className="w-6 h-6 text-cyan-400" />
                    <div>
                      <div className="text-gray-900 font-semibold">Bureau World Express</div>
                      <div className="text-gray-500 text-sm">Stop desk</div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                    formData.delivery_method === 'pickup'
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-gray-300 hover:border-blue-500'
                  }`}>
                    <input
                      type="radio"
                      name="delivery_method"
                      value="pickup"
                      checked={formData.delivery_method === 'pickup'}
                      onChange={handleChange}
                      className="text-cyan-400"
                      data-testid="pickup-option"
                    />
                    <Store className="w-6 h-6 text-cyan-400" />
                    <div>
                      <div className="text-gray-900 font-semibold">Retrait magasin</div>
                      <div className="text-gray-500 text-sm">Gratuit</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Address */}
              {formData.delivery_method !== 'pickup' && (
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    {formData.delivery_method === 'home_delivery' ? 'Adresse de livraison' : 'Wilaya de récupération'}
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-500 mb-2">Wilaya *</label>
                      <select
                        name="wilaya"
                        value={formData.wilaya}
                        onChange={handleChange}
                        required
                        className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:border-cyan-500 transition"
                        data-testid="wilaya-select"
                      >
                        <option value="">Sélectionnez une wilaya</option>
                        {WILAYAS.map(wilaya => (
                          <option key={wilaya} value={wilaya}>{wilaya}</option>
                        ))}
                      </select>
                    </div>
                    {formData.delivery_method === 'home_delivery' && (
                      <>
                        <div>
                          <label className="block text-gray-500 mb-2">Commune *</label>
                          <input
                            type="text"
                            name="commune"
                            value={formData.commune}
                            onChange={handleChange}
                            required
                            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:border-cyan-500 transition"
                            placeholder="Ex: Dar El Beida, Bab Ezzouar..."
                            data-testid="commune-input"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-500 mb-2">Adresse complète *</label>
                          <textarea
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            required
                            rows="3"
                            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:border-cyan-500 transition"
                            placeholder="Rue, quartier, n° immeuble..."
                            data-testid="address-input"
                          />
                        </div>
                      </>
                    )}
                    {formData.delivery_method === 'stop_desk' && (
                      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="text-blue-300 text-sm">
                          Votre colis sera disponible au bureau World Express de votre wilaya. 
                          Vous recevrez un SMS avec l'adresse exacte.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-500 text-white font-bold py-4 rounded-lg hover:bg-cyan-600 transition disabled:bg-blue-600 disabled:cursor-not-allowed"
                data-testid="submit-order-button"
              >
                {loading ? 'Traitement en cours...' : 'Confirmer la commande'}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 sticky top-20">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Résumé de la commande</h2>

              <div className="space-y-4 mb-6">
                {cartItems.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-500">
                      {item.name} x{item.quantity}
                    </span>
                    <span className="text-gray-900 font-semibold">
                      {(item.price * item.quantity).toLocaleString('fr-DZ')} DA
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between text-gray-500">
                  <span>Sous-total</span>
                  <span>{getCartTotal().toLocaleString('fr-DZ')} DA</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Frais de livraison</span>
                  <span className={shippingCost === 0 ? 'text-green-400' : ''}>
                    {shippingCost === 0 ? 'GRATUIT' : `${shippingCost.toLocaleString('fr-DZ')} DA`}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between text-gray-900 font-bold text-xl">
                  <span>Total</span>
                  <span data-testid="checkout-total">{getFinalTotal().toLocaleString('fr-DZ')} DA</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-blue-400 text-sm">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Paiement à la livraison ou en magasin
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;